package com.havn.app.core.supabase

import com.havn.app.core.logging.HavnLogger
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.annotations.SupabaseInternal
import io.github.jan.supabase.createSupabaseClient
import io.github.jan.supabase.auth.Auth
import io.github.jan.supabase.postgrest.Postgrest
import io.github.jan.supabase.storage.Storage
import io.ktor.client.plugins.api.createClientPlugin
import io.ktor.http.formUrlEncode
import io.ktor.util.AttributeKey
import io.ktor.util.date.getTimeMillis

@OptIn(SupabaseInternal::class)
object SupabaseClientProvider {
    val client: SupabaseClient by lazy {
        HavnLogger.info("Supabase", "Creating client for ${HavnSupabaseConfig.url}")
        createSupabaseClient(
            supabaseUrl = HavnSupabaseConfig.url,
            supabaseKey = HavnSupabaseConfig.anonKey
        ) {
            install(Auth) {
                scheme = havnAuthRedirectScheme
                host = havnAuthRedirectHost
                autoLoadFromStorage = false
                enableLifecycleCallbacks = false
            }
            install(Postgrest)
            install(Storage)
            httpConfig {
                install(HavnSlowSqlLoggerPlugin)
            }
        }
    }

    private val HavnSlowSqlLoggerPlugin = createClientPlugin("HavnSlowSqlLogger") {
        onRequest { request, _ ->
            request.attributes.put(SlowSqlStartedAtKey, getTimeMillis())
        }
        onResponse { response ->
            val startedAt = response.call.request.attributes.getOrNull(SlowSqlStartedAtKey) ?: return@onResponse
            val elapsedMs = getTimeMillis() - startedAt
            val path = response.call.request.url.encodedPath
            if (path.startsWith("/rest/v1/") && elapsedMs > SlowSqlWarningThresholdMs) {
                HavnLogger.warning(
                    "SupabaseSQL",
                    "Slow SQL ${response.call.request.method.value} ${response.call.request.sqlLogPath()} took ${elapsedMs}ms"
                )
            }
        }
    }

    private fun io.ktor.client.request.HttpRequest.sqlLogPath(): String {
        val query = url.parameters.formUrlEncode()
        val value = if (query.isBlank()) url.encodedPath else "${url.encodedPath}?$query"
        return value.take(SqlLogPathMaxLength).let { truncated ->
            if (value.length > SqlLogPathMaxLength) "$truncated..." else truncated
        }
    }

    private val SlowSqlStartedAtKey = AttributeKey<Long>("HavnSlowSqlStartedAt")
    private const val SlowSqlWarningThresholdMs = 1_000L
    private const val SqlLogPathMaxLength = 700
}
